import { X } from 'lucide-react';

import { cn } from '@/lib/utils';

interface InvitationRequiredModalProps {
  open: boolean;
  onClose: () => void;
  onOpenInviteModal: () => void;
  actionName?: string; // e.g., "open chests", "burn assets"
}

export function InvitationRequiredModal({
  open,
  onClose,
  onOpenInviteModal,
  actionName = 'use this feature',
}: InvitationRequiredModalProps) {
  if (!open) {
    return null;
  }

  const handleEnterCode = () => {
    onClose();
    onOpenInviteModal();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-md bg-card border border-border rounded-xl shadow-2xl animate-bounce-in">
        <button
          onClick={onClose}
          className={cn(
            'absolute top-4 right-4 p-1 rounded-full',
            'text-muted-foreground hover:text-foreground',
            'hover:bg-muted transition-colors'
          )}
        >
          <X size={20} />
        </button>

        <div className="p-6">
          <div className="text-center mb-6">
            <div className="mb-4">
              <div className="text-6xl">🎟️</div>
            </div>
            <h2
              className="text-2xl font-bold mb-2"
              style={{ fontFamily: 'Hyperspace Race Capsule, cursive', color: '#FEBB01' }}
            >
              INVITATION REQUIRED
            </h2>
            <p
              className="text-muted-foreground text-sm mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              You need an invitation code to {actionName}.
            </p>
            <div
              className="p-4 rounded-lg mb-4"
              style={{
                backgroundColor: 'rgba(254, 187, 1, 0.1)',
                border: '2px solid #FEBB01',
              }}
            >
              <p
                className="text-sm"
                style={{ fontFamily: 'Outfit, sans-serif', color: 'rgba(255, 255, 255, 0.9)' }}
              >
                Junk Fun is currently in invite-only mode. Enter your 5-character invitation code to unlock full access to all features.
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <button
              onClick={handleEnterCode}
              className="w-full py-3 px-8 font-medium transition-all transform hover:scale-105"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                fontSize: '18px',
                borderRadius: '100px',
                background:
                  'linear-gradient(92deg, #FB8B00 -4.02%, #F6DF6F 24.3%, #FB8B00 50.55%, #F6DF6F 72.17%, #FB8B00 103.05%)',
                boxShadow: '4px 4px 12px 0 rgba(0, 0, 0, 0.50)',
                color: '#000',
              }}
            >
              ENTER INVITATION CODE
            </button>

            <button
              onClick={onClose}
              className="w-full py-3 px-8 font-medium transition-all"
              style={{
                fontFamily: 'Hyperspace Race Capsule, cursive',
                fontSize: '16px',
                borderRadius: '100px',
                backgroundColor: '#6B7280',
                color: '#FFF',
              }}
            >
              CANCEL
            </button>
          </div>

          <div className="mt-6 pt-6 border-t">
            <p
              className="text-sm text-muted-foreground text-center mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Don't have an invitation code? Get one from:
            </p>
            <div className="flex justify-center gap-4">
              <a
                href="https://x.com/junkfun"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span className="text-lg">𝕏</span>
                <span className="text-sm">X</span>
              </a>
              <a
                href="https://discord.gg/junkfun"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span className="text-lg">💬</span>
                <span className="text-sm">Discord</span>
              </a>
              <a
                href="https://t.me/junkfun"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-muted hover:bg-muted/80 transition-colors"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                <span className="text-lg">✈️</span>
                <span className="text-sm">Telegram</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
